import { useI18n } from '../i18n/useI18n'
interface VolumeControlProps {
  volume: number
  muted: boolean
  onVolumeChange: (volume: number) => void
  onToggleMute: () => void
  className?: string
}

function SpeakerIcon({ muted }: { muted: boolean }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M4 9v6h4l5 4V5L8 9H4Z" />
      {muted ? (
        <path d="m17 9 4 6m0-6-4 6" />
      ) : (
        <path d="M16.5 8.5c1.8 1.8 1.8 5.2 0 7m2.5-9.5c3.2 3.2 3.2 8.8 0 12" />
      )}
    </svg>
  )
}

function ScreenIcon({ active }: { active: boolean }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <rect x="3" y="4" width="18" height="13" rx="2" />
      <path d="M8 21h8m-4-4v4" />
      <circle cx="17.5" cy="7.5" r="1.5" className={active ? 'filled-dot' : ''} />
    </svg>
  )
}

export function VolumeControl({
  volume,
  muted,
  onVolumeChange,
  onToggleMute,
  className = '',
}: VolumeControlProps) {
  const { t } = useI18n()
  return (
    <div className={`volume-control ${className}`.trim()} aria-label={t('playback.volumeControl')}>
      <button
        type="button"
        className="volume-icon-button"
        title={muted ? t('playback.unmute') : t('playback.mute')}
        aria-label={muted ? t('playback.unmute') : t('playback.mute')}
        aria-pressed={muted}
        onClick={onToggleMute}
      >
        <SpeakerIcon muted={muted} />
      </button>
      <input
        type="range"
        min="0"
        max="100"
        step="1"
        value={volume}
        aria-label={t('playback.volume')}
        onChange={(event) => onVolumeChange(Number(event.target.value))}
      />
      <output>{volume}%</output>
    </div>
  )
}

interface PracticePreferenceControlsProps extends VolumeControlProps {
  wakeSupported: boolean
  wakeEnabled: boolean
  wakeActive: boolean
  onWakeEnable: () => unknown | Promise<unknown>
  onWakeDisable: () => unknown | Promise<unknown>
}

export function PracticePreferenceControls({
  wakeSupported,
  wakeEnabled,
  wakeActive,
  onWakeEnable,
  onWakeDisable,
  ...volumeProps
}: PracticePreferenceControlsProps) {
  const { t } = useI18n()
  const wakeTitle = !wakeSupported
    ? t('playback.wakeUnsupported')
    : wakeEnabled
      ? wakeActive
        ? t('playback.wakeActive')
        : t('playback.wakeEnabled')
      : t('playback.wakeDisabled')
  const volumeTitle = volumeProps.muted
    ? t('playback.volumeMutedSummary', { volume: volumeProps.volume })
    : t('playback.volumeSummary', { volume: volumeProps.volume })

  return (
    <div className="practice-preference-controls" aria-label={t('playback.practiceSettings')}>
      <button
        type="button"
        className={`wake-toggle ${wakeEnabled ? 'enabled' : ''} ${wakeActive ? 'active' : ''}`.trim()}
        disabled={!wakeSupported}
        title={wakeTitle}
        aria-label={wakeTitle}
        aria-pressed={wakeEnabled}
        onClick={() => {
          if (wakeEnabled) void onWakeDisable()
          else void onWakeEnable()
        }}
      >
        <ScreenIcon active={wakeActive} />
      </button>

      <details className="volume-popover">
        <summary title={volumeTitle} aria-label={volumeTitle}>
          <SpeakerIcon muted={volumeProps.muted} />
        </summary>
        <VolumeControl {...volumeProps} className="popover-control" />
      </details>
    </div>
  )
}
